import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import BudgetAllocationIsInProgress from "../components/BudgetAllocationIsInProgress";
import {
  joinCampaignThunk,
  resetIsSavedDraftSuccess,
  resetError,
} from "@/features/campaigns/shared/store/campaignSlice";
import PageLayout from "@/components/layouts/page/PageLayout";
import NoDataFound from "@/components/NoDataFound";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import { canJoinCampaign, getDashboardPath } from "@/helpers/utils";
import {
  campaignErrorSelector as isErrorCampaignsSelector,
  campaignIsLoadingSelector as isLoadingCampaignsSelector,
  isSavedSelector,
} from "@/features/campaigns/shared/store/campaignSlice";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import JoinCampaignForm from "../components/JoinCampaignForm";
import { logger } from "@/helpers/loggerHelper";
import { HTTP_STATUS } from "@/config/constants";
import { Button } from "@mui/material";

type LoggedInUser = {
  id?: string | number;
  email?: string;
  participantData?: {
    id?: string | number;
  };
  [key: string]: unknown;
};

type SelectedBrand = {
  id?: string | number;
  [key: string]: unknown;
};

type CampaignData = {
  id?: string | number;
  is_brand_paid?: boolean;
  locations?: {
    subscribed?: unknown[];
    brandPaid?: unknown[];
    [key: string]: unknown;
  };
  campaign_advanced_setting?: unknown;
  [key: string]: unknown;
};

type CampaignResponse = {
  status?: number;
  data?: {
    rows?: CampaignData[];
    productList?: unknown;
  };
};

export default function LaunchDigitalCampaignPage() {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loggedInUser, selectedBrand } = useContext(GlobalContext) as {
    loggedInUser?: LoggedInUser;
    selectedBrand?: SelectedBrand;
  };
  const loader = useContext(LoaderContext) as (_isLoading: boolean) => void;

  const participantId =
    searchParams.get("participantId") || loggedInUser?.participantData?.id;

  const dispatch = useDispatch();
  const isSavedAsDraft = useSelector(isSavedSelector);
  const isError = useSelector(isErrorCampaignsSelector);
  const isProcessingCampaign = useSelector(isLoadingCampaignsSelector);

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [productList, setProductList] = useState(null);
  const [getAdvanceSettingData, setAdvancedSettingData] = useState(null);

  useEffect(() => {
    loader(loading || isProcessingCampaign);
  }, [loading, loader, isProcessingCampaign]);

  useEffect(() => {
    async function fetchCampaign() {
      setLoading(true);
      try {
        const campaignReqData = {
          id: campaignId,
          brand_id: selectedBrand?.id,
          fk_participant_id: participantId,
        };
        const res = (await campaignService.getSingleCampaign(
          campaignReqData
        )) as CampaignResponse;

        if (res.status === HTTP_STATUS.OK) {
          let campaignData = res.data?.rows[0];
          const getProductList = res.data?.productList;
          let campaignAdvancedSettings =
            campaignData?.campaign_advanced_setting;

          if (!campaignData) {
            setCampaign(null);
          } else {
            if (campaignData.is_brand_paid) {
              campaignData.locations.subscribed = campaignData.locations
                .brandPaid?.length
                ? campaignData.locations.brandPaid
                : campaignData.locations.subscribed;
            }

            setCampaign(campaignData);
            setProductList(getProductList);
            setAdvancedSettingData(campaignAdvancedSettings);
          }
        }
      } catch (err) {
        logger.error("Error in getCampaign", { err });
      } finally {
        setLoading(false);
      }
    }
    fetchCampaign();
  }, [campaignId, selectedBrand?.id, loggedInUser?.participantData?.id]);

  useEffect(() => {
    if (isSavedAsDraft) {
      (dispatch as any)(resetIsSavedDraftSuccess());

      if (isDraft) {
        navigate(`/launch-campaign`, {
          replace: true,
        });
      } else {
        navigate(`/participant/confirm`, {
          replace: true,
          state: { campaignId: campaignId, from: "join-campaign" },
        });
      }
    }
  }, [isSavedAsDraft, isDraft, dispatch, campaignId]);

  useEffect(() => {
    if (isError?.response?.data) {
      const redirect = isError?.response?.data?.redirect;
      if (redirect) {
        (dispatch as any)(resetError());
        navigate(`/launch-campaign`, {
          replace: true,
        });
      }
    }
  }, [isError, navigate, dispatch]);

  const getInitialValues = useMemo(() => {
    if (!campaign) return {};
    return joinCampaignHelper.getInitialValues(campaign);
  }, [campaign]);

  if (!campaign && !loading) {
    return (
      <PageLayout>
        <NoDataFound message={`No campaign found!`} />
      </PageLayout>
    );
  } else if (!canJoinCampaign(campaign) && !loading) {
    return (
      <PageLayout>
        <NoDataFound
          message={
            <div className="d-flex-col-center">
              <div>
                You have already joined this campaign or it is
                in-active/completed/not customizable.
              </div>
              <Button
                component={Link}
                to={`${getDashboardPath()}`}
                variant="contained"
                color="primary"
                className="my-4"
                size="large"
              >
                Back to dashboard
              </Button>
            </div>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout className="join-campaign-page">
      <BudgetAllocationIsInProgress campaignId={campaignId} />
      {loading ? (
        <NoDataFound message="Loading..." />
      ) : (
        <JoinCampaignForm
          campaign={campaign}
          initialValues={getInitialValues}
          loggedInUser={loggedInUser}
          submit={(data) => (dispatch as any)((joinCampaignThunk as any)(data))}
          setIsDraft={setIsDraft}
          selectedBrand={selectedBrand}
          productList={productList}
          getAdvanceSettingData={getAdvanceSettingData}
          isDraft={isDraft}
        />
      )}
    </PageLayout>
  );
}
